import {
    EventAllListenerFn,
    EventListenerFn,
    EventMap,
    EventOneOfListenerFn,
    FunctionArgsTernary, StrictEventEmitter
} from '@gongxigames/framework/core/services/strictEventEmitter/StrictEventEmitter';
import {StrictEventEmitterWrapperOptions} from '@gongxigames/framework/core/services/strictEventEmitter/SignalEventEmitterWrapper';

type ModuleEvents = `Module.${string}`;

export class ModuleSignalEventEmitterWrapper<T extends EventMap> {
    constructor(
        readonly emitter: StrictEventEmitter<T>,
        readonly options: StrictEventEmitterWrapperOptions
    ) {}

    /**
     * can listen to  events only with pattern 'Exclude{Module}.string'
     * @param args
     */
    on<K extends keyof T>(eventName: Exclude<K, `Module.${string}`>, cb: EventListenerFn<T, K>): void {
        this.emitter.on(eventName, cb, this.options);
    }

    /**
     * can listen to  events only with pattern 'Exclude{Module}.string'
     * @param args
     */
    once<K extends keyof T>(eventName: Exclude<K, `Module.${string}`>, cb: EventListenerFn<T, K>): void {
        this.emitter.once(eventName, cb, this.options);
    }

    /**
     * can listen to  events only with pattern 'Exclude{Module}.string'
     * @param args
     */
    onceAll<K extends keyof T, P extends K[]>(eventNames: Exclude<P, `Module.${string}`>, cb: EventAllListenerFn<T, P[number]>): () => void {
        return this.emitter.onceAll(eventNames, cb, this.options);
    }

    /**
     * can listen to  events only with pattern 'Exclude{Module}.string'
     * @param args
     */
    onceOneOf<K extends keyof T, P extends K[]>(eventNames: Exclude<P, `Module.${string}`>, cb: EventOneOfListenerFn<T, P[number]>): () => void {
        return this.emitter.onceOneOf(eventNames, cb, this.options);
    }

    /**
     * can emit only events with pattern 'Module.string'
     * @param args
     */
    emit<K extends keyof T & ModuleEvents, V extends T[K]>(...args: FunctionArgsTernary<V, [key: K, val: V], [key: K]>): void {
        this.emitter.emit(...args);
    }

    off<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>): void {
        return this.emitter.off(eventName, cb);
    }

    offByOwner<K extends keyof T>(eventName: K): void {
        const {owner} = this.options;

        if (!owner) {
            throw new Error('owner is missing');
        }

        return this.emitter.offByOwner(eventName, owner);
    }

    offAllByOwner(): void {
        const {owner} = this.options;

        if (!owner) {
            throw new Error('owner is missing');
        }

        return this.emitter.offAllByOwner(owner);
    }
}